import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProposals, type Proposal, type ProposalStatus, VALIDITY_OPTIONS, formatValidityDate } from '@/hooks/useProposals';
import { useTemplatePresets } from '@/hooks/useTemplatePresets';
import { useStorage, type Asset } from '@/hooks/useStorage';
import { getCategories, findActivePresetByCategory } from '@/mocks/template-presets';
import { generateProposalToken } from '@/mocks/template-config';
import { showToast } from '@/components/Toast';
import { findActiveAssets } from '@/mocks/assets-library';
import type { ProposalItem } from '@/mocks/proposals';

const COMPANY_ID = '3d7dc2cc-f843-4d6f-9a9b-af0fc9e1d7a4';

const UNIT_TYPES = [
  { value: 'un', label: 'Unidade' },
  { value: 'm', label: 'Metro' },
  { value: 'pc', label: 'Peça' },
  { value: 'kit', label: 'Kit' },
  { value: 'par', label: 'Par' },
  { value: 'conj', label: 'Conjunto' },
  { value: 'cx', label: 'Caixa' },
  { value: 'rol', label: 'Rolo' },
  { value: 'sv', label: 'Serviço' },
];

const STATUS_LABELS: Record<ProposalStatus, string> = {
  draft: 'Rascunho',
  sent: 'Enviada',
  viewed: 'Visualizada',
  approved: 'Já aprovada pessoalmente',
  rejected: 'Recusada',
  revision: 'Alteração Solicitada',
};

export default function ProposalFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getById, create, update, renew, loading } = useProposals();
  const { presets } = useTemplatePresets();
  const { listAssets } = useStorage();

  const [existing, setExisting] = useState<Proposal | undefined>(undefined);
  const [fetching, setFetching] = useState(!!id);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewDays, setRenewDays] = useState<number | null>(30);
  const [renewLoading, setRenewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // ====== MODAL DE IMAGEM ======
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState<'cover' | 'advantages'>('advantages');
  const [dbAssets, setDbAssets] = useState<Asset[]>([]);
  const [dbAssetsLoading, setDbAssetsLoading] = useState(false);
  const [imagePickerTab, setImagePickerTab] = useState<'library' | 'url'>('library');

  useEffect(() => {
    if (id) {
      getById(id).then((p) => {
        setExisting(p);
        setFetching(false);
      });
    }
  }, [id, getById]);

  const mode: 'create' | 'edit' = existing ? 'edit' : 'create';

  const handleRenew = async () => {
    if (!existing) return;
    setRenewLoading(true);
    try {
      await renew(existing.id, renewDays);
      showToast('Proposta renovada com sucesso!', 'success');
      setShowRenewModal(false);
      const refreshed = await getById(existing.id);
      if (refreshed) {
        setExisting(refreshed);
      }
    } catch {
      showToast('Erro ao renovar proposta. Tente novamente.', 'error');
    } finally {
      setRenewLoading(false);
    }
  };

  const [clientName, setClientName] = useState(existing?.client_name || '');
  const [clientCompany, setClientCompany] = useState(existing?.client_company || '');
  const [clientPhone, setClientPhone] = useState(existing?.client_phone || '');
  const [clientEmail, setClientEmail] = useState(existing?.client_email || '');
  const [clientAddress, setClientAddress] = useState(existing?.client_address || '');
  const [clientDocument, setClientDocument] = useState(existing?.client_document || '');
  const [category, setCategory] = useState(existing?.category || 'cameras');
  const [templatePresetId, setTemplatePresetId] = useState(existing?.template_preset_id || '');
  const [proposalNumber, setProposalNumber] = useState(existing?.proposal_number || '');
  const [validityDays, setValidityDays] = useState<number | null>(existing?.validity_days ?? 30);
  const [status, setStatus] = useState<ProposalStatus>(existing?.status || 'draft');
  const [description, setDescription] = useState(existing?.description || '');
  const [executionDeadline, setExecutionDeadline] = useState(existing?.execution_deadline || '');
  const [paymentTerms, setPaymentTerms] = useState(existing?.payment_terms || '');
  const [notes, setNotes] = useState(existing?.notes || '');
  const [advantagesText, setAdvantagesText] = useState(existing?.advantages_text || '');
  const [advantagesImage, setAdvantagesImage] = useState(existing?.advantages_image || '');
  const [coverImage, setCoverImage] = useState(existing?.cover_image || '');
  const [discount, setDiscount] = useState(existing?.discount || 0);
  const [serviceValue, setServiceValue] = useState(existing?.service_value || 0);
  const [installationValue, setInstallationValue] = useState(existing?.installation_value || 0);
  const [items, setItems] = useState<ProposalItem[]>(existing?.items || []);
  const [token] = useState(existing?.token || generateProposalToken());
  const [errors, setErrors] = useState<Record<string, string>>();

  const categories = getCategories();

  const categoryPresets = presets.filter((p) => p.category === category);

  // Filtro local dentro do picker
  const [pickerTypeFilter, setPickerTypeFilter] = useState<string>('all');
  const [pickerCategoryFilter, setPickerCategoryFilter] = useState<string>('');

  const pickerCategories = Array.from(new Set(dbAssets.map((a) => a.category).filter(Boolean)));

  const filteredDbAssets = dbAssets.filter((a) => {
    if (pickerTypeFilter !== 'all' && a.type !== pickerTypeFilter) return false;
    if (pickerCategoryFilter && a.category !== pickerCategoryFilter) return false;
    return true;
  });

  // Combina mock + assets do banco filtrados, preferindo o banco
  const mockAssets = findActiveAssets(category, 'main_image');
  const allLibraryImages = [
    ...filteredDbAssets.map((a) => ({ id: a.id, name: a.name, url: a.public_url })),
    ...mockAssets
      .filter((m) => !filteredDbAssets.some((d) => d.public_url === m.url))
      .map((m) => ({ id: m.id, name: m.name, url: m.url })),
  ];

  useEffect(() => {
    if (mode === 'create' && !existing) {
      const suggested = findActivePresetByCategory(category);
      if (suggested) {
        setTemplatePresetId(suggested.id);
      }
    }
  }, [category, mode, existing]);

  useEffect(() => {
    if (existing) {
      setClientName(existing.client_name || '');
      setClientCompany(existing.client_company || '');
      setClientPhone(existing.client_phone || '');
      setClientEmail(existing.client_email || '');
      setClientAddress(existing.client_address || '');
      setClientDocument(existing.client_document || '');
      setCategory(existing.category || 'cameras');
      setTemplatePresetId(existing.template_preset_id || '');
      setProposalNumber(existing.proposal_number || '');
      setValidityDays(existing.validity_days ?? 30);
      setStatus(existing.status || 'draft');
      setDescription(existing.description || '');
      setExecutionDeadline(existing.execution_deadline || '');
      setPaymentTerms(existing.payment_terms || '');
      setNotes(existing.notes || '');
      setAdvantagesText(existing.advantages_text || '');
      setAdvantagesImage(existing.advantages_image || '');
      setCoverImage(existing.cover_image || '');
      setDiscount(existing.discount || 0);
      setServiceValue(existing.service_value || 0);
      setInstallationValue(existing.installation_value || 0);
      setItems(existing.items || []);
    }
  }, [existing]);

  // Carrega imagens do banco quando o modal abrir — busca TODAS as imagens da biblioteca
  const loadDbAssets = async () => {
    setDbAssetsLoading(true);
    // Busca todas as imagens do banco, sem filtrar por tipo,
    // para permitir selecionar qualquer imagem da biblioteca
    const data = await listAssets();
    setDbAssets(data);
    setDbAssetsLoading(false);
  };

  // Reseta filtros do picker quando abre
  useEffect(() => {
    if (showImagePicker) {
      setPickerTypeFilter('all');
      setPickerCategoryFilter('');
      loadDbAssets();
    }
  }, [showImagePicker]);

  const handleCategoryChange = (cat: string) => {
    setCategory(cat);
    const suggested = findActivePresetByCategory(cat);
    setTemplatePresetId(suggested?.id || '');
    setAdvantagesImage('');
    // Se o cover_image não estiver setado, deixar o usuário escolher
    // Ou sugerir uma imagem da nova categoria
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unitPrice: 0, total: 0, unitType: 'un' }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof ProposalItem, value: string | number) => {
    const updated = items.map((item, i) => {
      if (i !== index) return item;
      const updatedItem = { ...item, [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
      }
      return updatedItem;
    });
    setItems(updated);
  };

  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal + serviceValue + installationValue - discount;

  const computedValidUntil = (() => {
    if (!validityDays) return 'Sem expiração';
    const now = new Date();
    const expires = new Date(now);
    expires.setDate(expires.getDate() + validityDays);
    return formatValidityDate(expires.toISOString());
  })();

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!clientName.trim()) newErrors.clientName = 'Nome do cliente é obrigatório';
    if (!proposalNumber.trim()) newErrors.proposalNumber = 'Código da proposta é obrigatório';
    if (!category) newErrors.category = 'Categoria é obrigatória';
    if (items.length === 0) newErrors.items = 'Adicione pelo menos um item';
    if (clientEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail.trim())) {
      newErrors.clientEmail = 'E-mail inválido';
    }
    if (clientPhone.trim() && clientPhone.trim().replace(/\D/g, '').length < 10) {
      newErrors.clientPhone = 'Telefone inválido. Informe DDD + número.';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showToast('Preencha os campos obrigatórios corretamente.', 'warning');
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitLoading(true);

    const data = {
      company_id: COMPANY_ID,
      client_name: clientName,
      client_company: clientCompany,
      client_phone: clientPhone,
      client_email: clientEmail,
      client_address: clientAddress,
      client_document: clientDocument,
      status,
      total_value: total,
      subtotal,
      discount,
      service_value: serviceValue,
      installation_value: installationValue,
      valid_until: computedValidUntil,
      items,
      category,
      template_preset_id: templatePresetId || null,
      proposal_number: proposalNumber,
      description,
      execution_deadline: executionDeadline,
      payment_terms: paymentTerms,
      notes,
      validity_days: validityDays,
      advantages_text: advantagesText,
      advantages_image: advantagesImage,
      cover_image: coverImage,
    };

    try {
      let savedProposal: Proposal;
      if (mode === 'edit' && existing) {
        const result = await update(existing.id, data);
        if (!result) throw new Error('Erro ao atualizar');
        savedProposal = result;
        showToast('Proposta atualizada com sucesso!', 'success');
      } else {
        const result = await create(data);
        savedProposal = result;
        showToast('Proposta criada com sucesso!', 'success');
      }

      // Enviar email se tiver email do cliente e status for 'sent' ou 'approved'
      if (savedProposal.client_email && (savedProposal.status === 'sent' || savedProposal.status === 'approved')) {
        try {
          const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
          const baseUrl = window.location.origin;
          const proposalUrl = `${baseUrl}/proposta/${savedProposal.token}`;
          await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: savedProposal.client_email,
              clientName: savedProposal.client_name,
              proposalNumber: savedProposal.proposal_number,
              proposalUrl,
              status: savedProposal.status,
            }),
          });
        } catch {
          // Silencioso — o email é um extra, não deve bloquear o fluxo
        }
      }

      navigate('/admin/proposals');
    } catch {
      showToast('Erro ao salvar proposta. Tente novamente.', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const inputClass = (error?: string) =>
    `w-full bg-brand-dark/50 border ${error ? 'border-red-500/50' : 'border-brand-gold/20'} rounded-md py-2 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50`;

  const labelClass = 'text-brand-text-muted text-xs mb-1 block';

  if (fetching || loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gray-700 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark">
      {/* Header */}
      <header className="h-14 bg-brand-darker border-b border-brand-gold/10 flex items-center justify-between px-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-file-edit-line text-brand-gold text-xl" />
          </div>
          <div>
            <h1 className="text-white font-semibold text-sm">
              {mode === 'create' ? 'Nova Proposta' : 'Editar Proposta'}
            </h1>
            <p className="text-brand-text-muted text-xs">Token: {token}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {mode === 'edit' && existing && (
            <button
              onClick={() => { setShowRenewModal(true); setRenewDays(existing.validity_days ?? 30); }}
              className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/20 hover:bg-brand-gold/10 cursor-pointer"
            >
              <i className="ri-refresh-line" />
              Renovar
            </button>
          )}
          <button
            onClick={() => navigate('/admin/proposals')}
            className="flex items-center gap-2 text-brand-text-muted hover:text-white text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Voltar
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 py-6">
        {/* Client Info */}
        <div className="bg-brand-card/30 border border-brand-gold/10 rounded-lg p-5 mb-4">
          <h3 className="text-brand-gold text-sm font-semibold mb-4 flex items-center gap-2">
            <i className="ri-user-line" />
            Dados do Cliente
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Cliente *</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className={inputClass(errors?.clientName)}
                placeholder="Nome do cliente"
              />
              {errors?.clientName && (
                <p className="text-red-400 text-xs mt-1">{errors.clientName}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Empresa</label>
              <input
                type="text"
                value={clientCompany}
                onChange={(e) => setClientCompany(e.target.value)}
                className={inputClass(errors?.clientCompany)}
                placeholder="Empresa do cliente (opcional)"
              />
              {errors?.clientCompany && (
                <p className="text-red-400 text-xs mt-1">{errors.clientCompany}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Telefone</label>
              <input
                type="text"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                className={inputClass(errors?.clientPhone)}
                placeholder="(44) 99999-9999"
              />
              {errors?.clientPhone && (
                <p className="text-red-400 text-xs mt-1">{errors.clientPhone}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>E-mail</label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                className={inputClass(errors?.clientEmail)}
                placeholder="cliente@empresa.com"
              />
              {errors?.clientEmail && (
                <p className="text-red-400 text-xs mt-1">{errors.clientEmail}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Endereço</label>
              <input
                type="text"
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                className={inputClass()}
                placeholder="Rua, número, bairro, cidade"
              />
            </div>
            <div>
              <label className={labelClass}>CPF / CNPJ</label>
              <input
                type="text"
                value={clientDocument}
                onChange={(e) => setClientDocument(e.target.value)}
                className={inputClass()}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
              />
            </div>
          </div>
        </div>

        {/* Category & Preset */}
        <div className="bg-brand-card/30 border border-brand-gold/10 rounded-lg p-5 mb-4">
          <h3 className="text-brand-gold text-sm font-semibold mb-4 flex items-center gap-2">
            <i className="ri-folder-image-line" />
            Categoria e Imagem da Capa
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelClass}>Categoria *</label>
              <select
                value={category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className={inputClass(errors?.category)}
              >
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
              {errors?.category && <p className="text-red-400 text-xs mt-1">{errors.category}</p>}
            </div>
            <div>
              <label className={labelClass}>Preset de Template</label>
              <select
                value={templatePresetId}
                onChange={(e) => setTemplatePresetId(e.target.value)}
                className={inputClass()}
              >
                <option value="">Nenhum (template padrão)</option>
                {categoryPresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="text-brand-text-muted text-xs mt-1">
                Preset sugerido automaticamente pela categoria.
              </p>
            </div>
          </div>
          
          {/* Imagem da Capa do PDF */}
          <div className="border-t border-brand-gold/10 pt-4">
            <label className={labelClass}>Imagem da Capa do PDF</label>
            <div className="flex items-center gap-3">
              {coverImage && (
                <img src={coverImage} alt="Capa" className="w-24 h-16 object-cover rounded-md border border-brand-gold/20" />
              )}
              <button
                onClick={() => { setImagePickerTarget('cover'); setShowImagePicker(true); }}
                className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/20 hover:bg-brand-gold/10 cursor-pointer"
              >
                <i className="ri-image-line" />
                {coverImage ? 'Trocar Imagem da Capa' : 'Selecionar Imagem da Capa'}
              </button>
              {coverImage && (
                <button
                  onClick={() => setCoverImage('')}
                  className="text-brand-text-muted hover:text-red-400 text-sm transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <p className="text-brand-text-muted text-xs mt-1">
              {coverImage ? 'Imagem personalizada selecionada.' : 'Se não selecionar, usa a imagem padrão da categoria.'}
            </p>
          </div>
        </div>

        {/* Proposal Details */}
        <div className="bg-brand-card/30 border border-brand-gold/10 rounded-lg p-5 mb-4">
          <h3 className="text-brand-gold text-sm font-semibold mb-4 flex items-center gap-2">
            <i className="ri-file-text-line" />
            Detalhes da Proposta
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className={labelClass}>Código da Proposta *</label>
              <input
                type="text"
                inputMode="text"
                autoComplete="off"
                data-form-type="other"
                value={proposalNumber}
                onChange={(e) => setProposalNumber(e.target.value)}
                className={inputClass(errors?.proposalNumber)}
                placeholder="Ex: 1000, 2001"
              />
              {errors?.proposalNumber && (
                <p className="text-red-400 text-xs mt-1">{errors.proposalNumber}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Validade da Proposta</label>
              <select
                value={validityDays ?? ''}
                onChange={(e) => setValidityDays(e.target.value === '' ? null : Number(e.target.value))}
                className={inputClass()}
              >
                {VALIDITY_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-brand-text-muted text-xs mt-1">
                Expira em: <span className="text-brand-gold">{computedValidUntil}</span>
              </p>
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ProposalStatus)}
                className={inputClass()}
              >
                {(Object.keys(STATUS_LABELS) as ProposalStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
              <p className="text-brand-text-muted text-xs mt-1">
                {status === 'approved' && 'Use este status se já combinou pessoalmente com o cliente.'}
                {status === 'sent' && 'A proposta será enviada por email ao cliente.'}
              </p>
            </div>
          </div>
          <div className="mb-4">
            <label className={labelClass}>Descrição / Resumo</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputClass()}
              rows={3}
              placeholder="Resumo do projeto ou proposta..."
              maxLength={500}
            />
            <span className="text-brand-text-muted text-xs">{description.length}/500</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Prazo de Execução</label>
              <input
                type="text"
                value={executionDeadline}
                onChange={(e) => setExecutionDeadline(e.target.value)}
                className={inputClass()}
                placeholder="30 dias úteis"
              />
            </div>
            <div>
              <label className={labelClass}>Condições de Pagamento</label>
              <input
                type="text"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className={inputClass()}
                placeholder="50% entrada + 50% na entrega"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className={labelClass}>Observações</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputClass()}
              rows={3}
              placeholder="Observações adicionais..."
              maxLength={500}
            />
            <span className="text-brand-text-muted text-xs">{notes.length}/500</span>
          </div>
        </div>

        {/* Vantagens do Investimento */}
        <div className="bg-brand-card/30 border border-brand-gold/10 rounded-lg p-5 mb-4">
          <h3 className="text-brand-gold text-sm font-semibold mb-4 flex items-center gap-2">
            <i className="ri-bar-chart-grouped-line" />
            Vantagens do Seu Investimento
          </h3>
          <div className="mb-4">
            <label className={labelClass}>Texto / Descrição das vantagens</label>
            <textarea
              value={advantagesText}
              onChange={(e) => setAdvantagesText(e.target.value)}
              className={inputClass()}
              rows={5}
              placeholder="Descreva as vantagens de cada produto e por que o cliente deve investir..."
              maxLength={1700}
            />
            <span className="text-brand-text-muted text-xs">{advantagesText.length}/1700</span>
          </div>
          <div>
            <label className={labelClass}>Imagem das Vantagens</label>
            <div className="flex items-center gap-3">
              {advantagesImage && (
                <img src={advantagesImage} alt="Vantagens" className="w-24 h-16 object-cover rounded-md border border-brand-gold/20" />
              )}
              <button
                onClick={() => { setImagePickerTarget('advantages'); setShowImagePicker(true); }}
                className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/20 hover:bg-brand-gold/10 cursor-pointer"
              >
                <i className="ri-image-line" />
                {advantagesImage ? 'Trocar Imagem' : 'Selecionar Imagem'}
              </button>
              {advantagesImage && (
                <button
                  onClick={() => setAdvantagesImage('')}
                  className="text-brand-text-muted hover:text-red-400 text-sm transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <p className="text-brand-text-muted text-xs mt-1">
              Imagem relacionada à categoria <span className="text-brand-gold">{category}</span>.
            </p>
          </div>
        </div>

        {/* Items */}
        <div className="bg-brand-card/30 border border-brand-gold/10 rounded-lg p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-brand-gold text-sm font-semibold flex items-center gap-2">
              <i className="ri-shopping-cart-line" />
              Itens da Proposta
            </h3>
            <button
              onClick={addItem}
              className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/20 hover:bg-brand-gold/10 cursor-pointer"
            >
              <i className="ri-add-line" />
              Adicionar Item
            </button>
          </div>

          {errors?.items && <p className="text-red-400 text-xs mb-3">{errors.items}</p>}

          {items.length === 0 ? (
            <p className="text-brand-text-muted text-sm text-center py-8">
              Nenhum item adicionado. Clique em "Adicionar Item" para começar.
            </p>
          ) : (
            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start bg-brand-dark/30 rounded-md p-3"
                >
                  <div className="sm:col-span-4">
                    <label className={labelClass}>Descrição</label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      className={inputClass()}
                      placeholder="Descrição do item"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Unidade</label>
                    <select
                      value={item.unitType}
                      onChange={(e) => updateItem(index, 'unitType', e.target.value)}
                      className={inputClass()}
                    >
                      {UNIT_TYPES.map((u) => (
                        <option key={u.value} value={u.value}>{u.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-1">
                    <label className={labelClass}>Qtd</label>
                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) =>
                        updateItem(index, 'quantity', parseInt(e.target.value) || 1)
                      }
                      className={inputClass()}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Unitário</label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={item.unitPrice}
                      onChange={(e) =>
                        updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)
                      }
                      className={inputClass()}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Total</label>
                    <input
                      type="text"
                      value={`R$ ${item.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                      disabled
                      className="w-full bg-brand-dark/30 border border-brand-gold/10 rounded-md py-2 px-3 text-brand-gold text-sm"
                    />
                  </div>
                  <div className="sm:col-span-1 flex items-end justify-end h-full">
                    <button
                      onClick={() => removeItem(index)}
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-500/10 text-brand-text-muted hover:text-red-400 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          <div className="mt-4 pt-4 border-t border-brand-gold/10 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
            <div className="space-y-2 w-full sm:w-auto">
              <div className="flex items-center gap-3">
                <span className="text-brand-text-muted text-sm">Subtotal:</span>
                <span className="text-brand-text text-sm font-medium">
                  R$ {subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-brand-text-muted text-sm">Serviços:</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={serviceValue}
                  onChange={(e) => setServiceValue(parseFloat(e.target.value) || 0)}
                  className="w-32 bg-brand-dark/50 border border-brand-gold/20 rounded-md py-1 px-2 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-brand-text-muted text-sm">Desconto:</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={discount}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  className="w-32 bg-brand-dark/50 border border-brand-gold/20 rounded-md py-1 px-2 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                />
              </div>
            </div>
            <div className="text-right w-full sm:w-auto">
              <span className="text-brand-text-muted text-sm">Total:</span>
              <p className="text-brand-gold text-xl font-bold">
                R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 justify-end">
          <button
            onClick={() => navigate('/admin/proposals')}
            className="px-6 py-2.5 rounded-md border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitLoading}
            className="px-6 py-2.5 rounded-md bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {submitLoading ? (
              <div className="w-5 h-5 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" />
            ) : (
              mode === 'create' ? 'Criar Proposta' : 'Salvar Alterações'
            )}
          </button>
        </div>
      </div>

      {/* Image Picker Modal — INTEGRADO COM BANCO + MOCK */}
      {showImagePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowImagePicker(false)} />
          <div className="relative bg-brand-card border border-brand-gold/30 rounded-lg p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-brand-gold text-lg font-semibold">Selecionar Imagem</h3>
              <button
                onClick={() => setShowImagePicker(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-brand-gold/10 text-brand-text-muted hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            {/* Abas */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setImagePickerTab('library')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  imagePickerTab === 'library'
                    ? 'bg-brand-gold text-brand-dark'
                    : 'bg-brand-dark/50 text-brand-text-muted border border-brand-gold/10 hover:text-white'
                }`}
              >
                <i className="ri-folder-image-line mr-1" />
                Biblioteca ({allLibraryImages.length})
              </button>
              <button
                onClick={() => setImagePickerTab('url')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  imagePickerTab === 'url'
                    ? 'bg-brand-gold text-brand-dark'
                    : 'bg-brand-dark/50 text-brand-text-muted border border-brand-gold/10 hover:text-white'
                }`}
              >
                <i className="ri-link mr-1" />
                URL Direta
              </button>
            </div>

            {/* Aba Biblioteca */}
            {imagePickerTab === 'library' && (
              <div>
                {/* Filtros */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="flex-1">
                    <label className="text-brand-text-muted text-[10px] uppercase tracking-wider mb-1 block">Filtrar por Tipo</label>
                    <select
                      value={pickerTypeFilter}
                      onChange={(e) => setPickerTypeFilter(e.target.value)}
                      className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-1.5 px-2 text-brand-text text-xs focus:outline-none focus:border-brand-gold/50"
                    >
                      <option value="all">Todos os tipos</option>
                      <option value="top_banner_desktop">Banner Desktop</option>
                      <option value="top_banner_mobile">Banner Mobile</option>
                      <option value="main_image">Imagem Principal</option>
                      <option value="pdf_cover">Capa PDF</option>
                      <option value="pdf_back_cover">Contracapa PDF</option>
                      <option value="footer_banner">Rodapé</option>
                      <option value="logo">Logo</option>
                      <option value="qr_code">QR Code</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-brand-text-muted text-[10px] uppercase tracking-wider mb-1 block">Filtrar por Categoria</label>
                    <select
                      value={pickerCategoryFilter}
                      onChange={(e) => setPickerCategoryFilter(e.target.value)}
                      className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-1.5 px-2 text-brand-text text-xs focus:outline-none focus:border-brand-gold/50"
                    >
                      <option value="">Todas as categorias</option>
                      {pickerCategories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => { setPickerTypeFilter('all'); setPickerCategoryFilter(''); }}
                      className="text-brand-text-muted hover:text-brand-gold text-xs transition-colors cursor-pointer whitespace-nowrap px-2 py-1.5"
                    >
                      <i className="ri-refresh-line mr-1" />
                      Limpar filtros
                    </button>
                  </div>
                </div>

                <p className="text-brand-text-muted text-xs mb-3">
                  Mostrando <span className="text-brand-gold">{allLibraryImages.length}</span> imagens
                  {pickerTypeFilter !== 'all' && <> do tipo <span className="text-brand-gold">{pickerTypeFilter}</span></>}
                  {pickerCategoryFilter && <> da categoria <span className="text-brand-gold">{pickerCategoryFilter}</span></>}
                  :
                </p>
                {dbAssetsLoading && (
                  <div className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-gray-700 border-t-amber-500 rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-brand-text-muted text-xs">Carregando do banco...</p>
                  </div>
                )}
                {!dbAssetsLoading && allLibraryImages.length === 0 && (
                  <div className="text-center py-8 border border-brand-gold/10 rounded-lg">
                    <i className="ri-image-line text-brand-gold/30 text-3xl mb-2" />
                    <p className="text-brand-text-muted text-sm">Nenhuma imagem disponível.</p>
                    <p className="text-brand-text-muted text-xs mt-1">
                      Cadastre imagens em <strong>Admin &gt; Biblioteca</strong>.
                    </p>
                    <p className="text-brand-text-muted text-xs mt-1">
                      Dica: use o tipo <span className="text-brand-gold">"Imagem Principal"</span> e a categoria <span className="text-brand-gold">"{category}"</span> para aparecer aqui por padrão.
                    </p>
                  </div>
                )}
                {!dbAssetsLoading && allLibraryImages.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                    {allLibraryImages.map((asset) => {
                      const isSelected = imagePickerTarget === 'cover'
                        ? coverImage === asset.url
                        : advantagesImage === asset.url;
                      return (
                        <button
                          key={asset.id}
                          onClick={() => {
                            if (imagePickerTarget === 'cover') {
                              setCoverImage(asset.url);
                            } else {
                              setAdvantagesImage(asset.url);
                            }
                            setShowImagePicker(false);
                          }}
                          className={`relative aspect-video rounded-md overflow-hidden border-2 transition-all cursor-pointer ${
                            isSelected
                              ? 'border-brand-gold ring-2 ring-brand-gold/30'
                              : 'border-brand-gold/20 hover:border-brand-gold/50 hover:scale-[1.02]'
                          }`}
                        >
                          <img src={asset.url} alt={asset.name} className="w-full h-full object-cover" />
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                            <p className="text-white text-[10px] truncate">{asset.name}</p>
                          </div>
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-brand-gold rounded-full flex items-center justify-center">
                              <i className="ri-check-line text-brand-dark text-xs font-bold" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Aba URL */}
            {imagePickerTab === 'url' && (
              <div>
                <label className="text-brand-text-muted text-xs mb-1 block">Cole a URL da imagem:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={imagePickerTarget === 'cover' ? coverImage : advantagesImage}
                    onChange={(e) => {
                      if (imagePickerTarget === 'cover') {
                        setCoverImage(e.target.value);
                      } else {
                        setAdvantagesImage(e.target.value);
                      }
                    }}
                    className="flex-1 bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                    placeholder="https://..."
                  />
                  <button
                    onClick={() => setShowImagePicker(false)}
                    className="px-4 py-2 rounded-md bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    OK
                  </button>
                </div>
                {(imagePickerTarget === 'cover' ? coverImage : advantagesImage) && (
                  <div className="mt-4">
                    <p className="text-brand-text-muted text-xs mb-1">Preview:</p>
                    <img
                      src={imagePickerTarget === 'cover' ? coverImage : advantagesImage}
                      alt="Preview"
                      className="w-48 h-32 object-cover rounded-md border border-brand-gold/20"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Renew Modal */}
      {showRenewModal && existing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowRenewModal(false)} />
          <div className="relative bg-brand-card border border-brand-gold/30 rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-brand-gold text-lg font-semibold mb-1">Renovar Proposta</h3>
            <p className="text-brand-text-muted text-xs mb-4">
              {existing.proposal_number} — {existing.client_name}
            </p>
            <div className="mb-4">
              <label className="text-brand-text-muted text-xs mb-1 block">Nova validade</label>
              <select
                value={renewDays ?? ''}
                onChange={(e) => setRenewDays(e.target.value === '' ? null : Number(e.target.value))}
                className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
              >
                {VALIDITY_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-brand-text-muted text-xs mt-2">
                A proposta será renovada a partir da data atual.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRenewModal(false)}
                className="flex-1 border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 py-2 rounded-md text-sm transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleRenew}
                disabled={renewLoading}
                className="flex-1 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-semibold py-2 rounded-md text-sm transition-colors cursor-pointer disabled:opacity-50"
              >
                {renewLoading ? 'Renovando...' : 'Renovar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}